import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import StudentCard from "../shared/StudentCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Grid, List } from "lucide-react";
import { toast } from "sonner";

const StudentManagementAdmin = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    gender: "",
    dob: "",
    class_name: "",
    section: "",
    room_number: "",
    father_name: "",
    mother_name: "",
    primary_phone: "",
    secondary_phone: "",
    parent_email: "",
    local_guardian_name: "",
    local_guardian_phone: "",
    address: "",
    blood_group: "",
    interests: "",
    known_diseases: ""
  });
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/classes/?hostel_id=${user.hostel_id}`);
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load classes:', error);
    }
  };

  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const fetchStudents = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/students/?hostel_id=${user.hostel_id}`);
      console.log('📊 Students data received:', response.data);
      setStudents(response.data);
    } catch (error) {
      toast.error("Failed to fetch students");
      console.error('❌ Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {

      if(formData?.age < 0){
        toast.error("Age cannot be negative");
        return;
      }
      // max age limit
      if(formData?.age > 100){
        toast.error("Age cannot be greater than 100");
        return;
      }
      const payload = {
        name: formData.name,
        age: parseInt(formData.age),
        gender: formData.gender || null,
        dob: formData.dob || null,
        class_name: formData.class_name,
        section: formData.section || null,
        room_number: formData.room_number || null,
        father_name: formData.father_name,
        mother_name: formData.mother_name || null,
        primary_phone: formData.primary_phone,
        secondary_phone: formData.secondary_phone || null,
        parent_email: formData.parent_email,
        local_guardian_name: formData.local_guardian_name || null,
        local_guardian_phone: formData.local_guardian_phone || null,
        address: formData.address,
        blood_group: formData.blood_group,
        interests: formData.interests ? formData.interests.split(",").map(i => i.trim()).filter(Boolean) : [],
        known_diseases: formData.known_diseases || null,
        hostel_id: user.hostel_id
      };
      
      const response = await apiClient.post('/v1/admin/students/', payload);
      
      // Show generated password
      if (response.data.parent_password) {
        setGeneratedPassword(response.data.parent_password);
        setShowPassword(true);
        fetchStudents();
      } else {
        toast.success("Student added successfully!");
        setDialogOpen(false);
        fetchStudents();
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add student");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      age: "",
      gender: "",
      dob: "",
      class_name: "",
      section: "",
      room_number: "",
      father_name: "",
      mother_name: "",
      primary_phone: "",
      secondary_phone: "",
      parent_email: "",
      local_guardian_name: "",
      local_guardian_phone: "",
      address: "",
      blood_group: "",
      interests: "",
      known_diseases: ""
    });
    setGeneratedPassword("");
    setShowPassword(false);
  };

  const handleDelete = async (student) => {
    if (!window.confirm(`Are you sure you want to delete ${student.name}?`)) return;
    
    try {
      await apiClient.delete(`/v1/admin/students/${student.id}`);
      toast.success("Student deleted successfully!");
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete student");
    }
  };

  const handleEdit = (student) => {
    // Navigate to edit page or open edit dialog
    navigate(`/admin/students/edit/${student.id}`);
  };

  const handleAssign = (student) => {
    // Show assignment options
    const assignmentType = window.confirm('Assign Warden? (Click OK for Warden, Cancel for Parent)');
    if (assignmentType) {
      // Assign Warden
      navigate(`/admin/student-assignment?type=warden&student_id=${student.id}`);
    } else {
      // Assign Parent
      navigate(`/admin/student-assignment?type=parent&student_id=${student.id}`);
    }
  };

  const handleConnect = (student) => {
    // Show parent contact information
    if (student.parent_info) {
      toast.info(`Parent: ${student.parent_info.name} | Phone: ${student.parent_info.phone}`);
    } else {
      toast.error("Parent information not available");
    }
  };

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
    student.class_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.room_number?.includes(searchTerm)
  );

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="loading-spinner"></div></div>;
  }

  return (
    <div data-testid="student-management-admin" className="space-y-6">
      {/* banner if have no students */}
      {classes.length === 0 && (
        <div className="bg-blue-100 border-t-4 border-blue-500 rounded-b text-blue-900 px-4 py-3 shadow-md" role="alert">
          <div className="flex flex-col gap-3">
            <div>
              <p className="font-bold">Notice</p>
              <p className="text-sm">Before adding new students, you need to add at least one class.</p>
            </div>
            <div className="flex gap-3"> 
              <Button className="btn-warning text-white" onClick={() => navigate("/admin/classes")}>Add Class</Button>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>Student Management</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-student-btn" className="btn-primary text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Student</DialogTitle>
            </DialogHeader>
            
            {!showPassword ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Student Information */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-gray-700">Student Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Student Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="age">Age *</Label>
                      <Input
                        id="age"
                        type="number"
                        value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        required
                        placeholder="Age"
                      />
                    </div>
                    <div>
                      <Label htmlFor="class">Class *</Label>
                      <select
                        id="class"
                        value={formData.class_name}
                        onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Class</option>
                        {classes.map((cls) => (
                          <option key={cls.id} value={cls.name}>
                            {cls.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="section">Section</Label>
                      <Input
                        id="section"
                        value={formData.section}
                        onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                        placeholder="e.g., A, B (optional)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="room">Room Number</Label>
                      <Input
                        id="room"
                        value={formData.room_number}
                        onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                        placeholder="Room number (optional)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender *</Label>
                      <select
                        id="gender"
                        value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="dob">Birth Date *</Label>
                      <Input
                        id="dob"
                        type="date"
                        value={formData.dob}
                        onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="blood_group">Blood Group *</Label>
                      <select
                        id="blood_group"
                        value={formData.blood_group}
                        onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Blood Group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Parent Information */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-gray-700">Parent Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="father_name">Father Name *</Label>
                      <Input
                        id="father_name"
                        value={formData.father_name}
                        onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                        required
                        placeholder="Father's full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mother_name">Mother Name</Label>
                      <Input
                        id="mother_name"
                        value={formData.mother_name}
                        onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                        placeholder="Mother's full name (optional)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="primary_phone">Primary Phone *</Label>
                      <Input
                        id="primary_phone"
                        type="tel"
                        value={formData.primary_phone}
                        onChange={(e) => setFormData({ ...formData, primary_phone: e.target.value })}
                        required
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="secondary_phone">Secondary Phone</Label>
                      <Input
                        id="secondary_phone"
                        type="tel"
                        value={formData.secondary_phone}
                        onChange={(e) => setFormData({ ...formData, secondary_phone: e.target.value })}
                        placeholder="Alternate phone (optional)"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="parent_email">Parent Email *</Label>
                      <Input
                        id="parent_email"
                        type="email"
                        value={formData.parent_email}
                        onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                        required
                        placeholder="parent@example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Local Guardian Information */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-gray-700">Local Guardian (Optional)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="local_guardian_name">Local Guardian Name</Label>
                      <Input
                        id="local_guardian_name"
                        value={formData.local_guardian_name}
                        onChange={(e) => setFormData({ ...formData, local_guardian_name: e.target.value })}
                        placeholder="Guardian's name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="local_guardian_phone">Local Guardian Phone</Label>
                      <Input
                        id="local_guardian_phone"
                        type="tel"
                        value={formData.local_guardian_phone}
                        onChange={(e) => setFormData({ ...formData, local_guardian_phone: e.target.value })}
                        placeholder="Guardian's phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-gray-700">Additional Information</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address">Address *</Label>
                      <textarea
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        required
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Full residential address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="interests">Interests</Label>
                      <Input
                        id="interests"
                        value={formData.interests}
                        onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                        placeholder="e.g., Sports, Music, Art (optional)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="known_diseases">Known Diseases/Allergies</Label>
                      <Input
                        id="known_diseases"
                        value={formData.known_diseases}
                        onChange={(e) => setFormData({ ...formData, known_diseases: e.target.value })}
                        placeholder="Any medical conditions (optional)"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> A parent account will be automatically created with the Primary Phone as login ID. 
                    The generated password will be shown after submission.
                  </p>
                </div>

                <Button type="submit" className="w-full btn-primary text-white">
                  Add Student & Create Parent Account
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 mb-2">✅ Student Added Successfully!</h3>
                  <p className="text-sm text-green-700">Parent account has been created with the following credentials:</p>
                </div>
                
                <div className="bg-white border-2 border-gray-300 rounded-lg p-4 space-y-3">
                  <div>
                    <Label className="text-xs text-gray-600">Login ID (Phone Number)</Label>
                    <div className="font-mono text-lg font-semibold text-gray-900 mt-1">{formData.primary_phone}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Generated Password</Label>
                    <div className="font-mono text-lg font-semibold text-blue-600 bg-blue-50 px-3 py-2 rounded border border-blue-200 mt-1">
                      {generatedPassword}
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Important:</strong> Please save this password and share it with the parent. 
                    This password will not be shown again.
                  </p>
                </div>

                <Button 
                  onClick={() => {
                    setDialogOpen(false);
                    fetchStudents();
                    resetForm();
                  }}
                  className="w-full btn-primary text-white"
                >
                  Close & Refresh
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="glass-effect p-4 rounded-lg">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="search-students-input"
            placeholder="Search students by name, class, or room..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-end mb-4">
        <div className="flex gap-2 bg-white rounded-lg p-1 shadow">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-2 rounded transition-colors ${
              viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 rounded transition-colors ${
              viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Students Grid/List */}
      <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' : 'grid grid-cols-1 gap-4'}>
        {filteredStudents.map((student) => (
          <StudentCard
            key={student.id}
            student={student}
            userRole="admin"
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAssign={handleAssign}
            onConnect={handleConnect}
          />
        ))}
      </div>

      {filteredStudents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No students found</p>
        </div>
      )}
    </div>
  );
};

export default StudentManagementAdmin;
